import React from 'react';
import loadable from '@loadable/component';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';

import { H1 } from '../PageBuilder/Primitives/Heading';
import FallbackPage, { fallbackSections } from './FallbackPage';
import { ASSET_NAME } from './PrivacyPolicyPage.duck';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);
const SectionBuilder = loadable(
  () => import(/* webpackChunkName: "SectionBuilder" */ '../PageBuilder/PageBuilder'),
  {
    resolveComponent: components => components.SectionBuilder,
  }
);

const LEADFLOW_NOTICE_MARKDOWN = `
**Important – LeadFlow and Marketplace Services**

If you use our LeadFlow platform or marketplace features, additional information about how your personal data is used, including how it may be shared with suppliers and derived from planning data sources, is set out in our **Privacy Policy Addendum (LeadFlow & Marketplace Functionality)** at the end of this document.
`;

const LEADFLOW_PRIVACY_ADDENDUM_MARKDOWN = `
*LeadFlow Platform, Planning Data & Marketplace Functionality*

## 1. Introduction

This Addendum forms part of the Privacy Notice of The Modern Builder Limited ("we", "us", "our") and applies specifically to the use of our LeadFlow platform, marketplace services, and planning data integrations.

This Addendum should be read alongside our main Privacy Notice. In the event of any inconsistency, this Addendum shall take precedence in relation to the services described herein.

## 2. Planning Data and Public Data Sources

We may collect and process personal data obtained from publicly available sources, including planning application data published by local authorities and planning data providers such as the Planning Portal.

This data may include:

- applicant names
- site addresses
- planning application references
- project descriptions and associated documents

We use this data to:

- identify construction and development opportunities;
- support users in planning and delivering projects;
- match users with relevant suppliers and service providers;
- validate or enhance information submitted by users.

Where we combine publicly available data with user-provided data, this may result in the creation of enriched project or customer profiles.

The lawful basis for this processing is our legitimate interests in operating and improving our platform and services.

## 3. Marketplace Functionality and Supplier Introductions

Our platform operates as a marketplace connecting customers (including self-builders, developers, and property owners) with third-party construction professionals, suppliers, and service providers ("Suppliers").

When you use the platform:

- your project details, enquiry data, and contact information may be shared with relevant Suppliers;
- Suppliers may access and review your information to assess your project;
- Suppliers may contact you directly using the contact details you have provided.

Suppliers you engage with through the platform act as independent data controllers in respect of any personal data they receive.

This means:

- they determine how your personal data is used once shared;
- their use of your data is subject to their own privacy policies.

We require Suppliers to process personal data in accordance with applicable data protection laws.

## 4. Lead Generation, Matching and Profiling

We process personal data to facilitate lead generation and matching between customers and Suppliers.

This includes:

- analysing project details, behaviour, and platform interactions;
- assessing levels of interest or readiness to engage with Suppliers;
- prioritising or recommending projects to Suppliers;
- improving the relevance of supplier matches.

This may involve automated processing and profiling.

We do not carry out solely automated decision-making that produces legal or similarly significant effects.

The lawful basis for this processing is our legitimate interests in operating an efficient and effective marketplace.

## 5. Platform Messaging and Communications

Our platform enables communication between users and Suppliers.

When using messaging features:

- messages may be stored and processed by us;
- messages may be accessible to the relevant Supplier(s) involved in the conversation;
- communications may be used to support service delivery, quality assurance, and platform improvements.

We may monitor communications where reasonably necessary for:

- security purposes;
- fraud prevention;
- compliance with our terms and policies.

## 6. Supplier Communications

As part of the core service provided by our platform:

- Suppliers may contact you directly in relation to your project or enquiry;
- such communications are considered part of the service you have requested.

These communications are distinct from general marketing and are necessary to facilitate connections between customers and Suppliers.

## 7. Data Sharing and Responsibility

Where personal data is shared with Suppliers:

- we act as the data controller up to the point of sharing;
- Suppliers act as independent data controllers thereafter.

We take reasonable steps to ensure that data is only shared where necessary and appropriate safeguards are in place.

However, we are not responsible for how Suppliers use personal data once they receive it.

## 8. Lawful Basis Summary

Processing under this Addendum is primarily based on:

- **Legitimate interests** – operating the platform, matching users and Suppliers, analysing planning data;
- **Contractual necessity** – delivering services requested by users;
- **Consent** – where required for marketing communications.

We ensure that our legitimate interests are balanced against your rights and freedoms.

## 9. Updates to this Addendum

We may update this Addendum from time to time to reflect changes to our platform, services, or legal obligations.
`;

const leadflowNoticeSection = {
  sectionType: 'article',
  sectionId: 'leadflow-privacy-notice',
  appearance: { fieldType: 'customAppearance', backgroundColor: '#f8f9fa' },
  blocks: [
    {
      blockType: 'defaultBlock',
      blockId: 'leadflow-privacy-notice-content',
      text: {
        fieldType: 'markdown',
        content: LEADFLOW_NOTICE_MARKDOWN,
      },
    },
  ],
};

const leadflowPrivacyAddendumSection = {
  sectionType: 'article',
  sectionId: 'leadflow-privacy-addendum',
  appearance: { fieldType: 'customAppearance', backgroundColor: '#ffffff' },
  title: { fieldType: 'heading1', content: 'PRIVACY POLICY ADDENDUM' },
  blocks: [
    {
      blockType: 'defaultBlock',
      blockId: 'leadflow-privacy-addendum-content',
      text: {
        fieldType: 'markdown',
        content: LEADFLOW_PRIVACY_ADDENDUM_MARKDOWN,
      },
    },
  ],
};

// This "content-only" component can be used in modals etc.
const PrivacyPolicyContent = props => {
  const { inProgress, error, data } = props;

  if (inProgress) {
    return null;
  }

  // We don't want to add h1 heading twice to the HTML (SEO issue).
  // Modal's header is mapped as h2
  const hasContent = data => typeof data?.content === 'string';
  const exposeContentAsChildren = data => {
    return hasContent(data) ? { children: data.content } : {};
  };
  const CustomHeading1 = props => <H1 as="h2" {...props} />;

  const hasData = error === null && data;
  const sectionsData = hasData ? data : fallbackSections;

  return (
    <SectionBuilder
      {...sectionsData}
      options={{
        fieldComponents: {
          heading1: { component: CustomHeading1, pickValidProps: exposeContentAsChildren },
        },
        isInsideContainer: true,
      }}
    />
  );
};

// Presentational component for PrivacyPolicyPage
const PrivacyPolicyPageComponent = props => {
  const { pageAssetsData, inProgress, error } = props;

  const basePageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;
  const enrichedPageData = basePageData
    ? (() => {
        const cmsSections = basePageData.sections || [];
        // Insert the LeadFlow notice after the first CMS section ("About this Privacy Notice")
        const withNotice =
          cmsSections.length > 0
            ? [cmsSections[0], leadflowNoticeSection, ...cmsSections.slice(1)]
            : [leadflowNoticeSection];
        return {
          ...basePageData,
          sections: [...withNotice, leadflowPrivacyAddendumSection],
        };
      })()
    : basePageData;

  return (
    <PageBuilder
      pageAssetsData={enrichedPageData}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage />}
    />
  );
};

PrivacyPolicyPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const PrivacyPolicyPage = compose(connect(mapStateToProps))(PrivacyPolicyPageComponent);

const PRIVACY_POLICY_ASSET_NAME = ASSET_NAME;
export { PRIVACY_POLICY_ASSET_NAME, PrivacyPolicyPageComponent, PrivacyPolicyContent };

export default PrivacyPolicyPage;
